import React, { useEffect, useState } from "react";
import { API } from "../utils/api";
import {
  Plus,
  Minus,
  Search,
  Loader2,
  AlertCircle,
  UserCheck,
  UserX,
  Wallet,
  RefreshCw,
  X,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Gift,
  DollarSign,
  Clock,
  XCircle,
  CheckCircle,
} from "lucide-react";
import { Edit, Trash2 } from "lucide-react";

import ClientDetailsModal from "../pages/ViewClient";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [processing, setProcessing] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);


  // Money Modal
  const [moneyModal, setMoneyModal] = useState({
    open: false,
    type: "",
    client: null,
    amount: "",
    note: "",
  });

  const [viewModal, setViewModal] = useState({
    open: false,
    client: null,
  });

  // Transactions Modal
  const [txModal, setTxModal] = useState({
    open: false,
    client: null,
    transactions: [],
    loading: false,
    txSearch: "",
    typeFilter: "all",
  });

  // Load clients
  const loadClients = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/admin/clients");
      setClients(res.data || []);
      setFilteredClients(res.data || []);
    } catch {
      setError("Failed to load clients.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  // Search clients
  useEffect(() => {
    const q = (search || "").toLowerCase().trim();
    setFilteredClients(
      clients.filter((c) => {
        const rc = (c.referralCode || "").toLowerCase();
        return (
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          rc.includes(q)
        );
      })
    );
  }, [search, clients]);

  // Toggle status
  const toggleStatus = async (id, current) => {
    setStatusUpdatingId(id);
    setProcessing(true);
    try {
      await API.put(`/admin/client/${id}/status`, { status: !current });
      await loadClients();
    } catch {
      setError("Failed to update status.");
    } finally {
      setProcessing(false);
      setStatusUpdatingId(null);
    }
  };


  const handleDeleteClient = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    try {
      await API.delete(`/admin/client/${id}`);
      alert('Client Deleted Successfully');
      await loadClients();
    } catch (err) {
      console.error(err);
      setError("Failed to delete client");
    }
  };


  // Money Modal
  const openMoneyModal = (type, client) =>
    setMoneyModal({ open: true, type, client, amount: "", note: "" });

  const handleMoney = async () => {
    const amount = parseFloat(moneyModal.amount);
    if (!amount || amount <= 0) {
      setError("Enter a valid amount");
      return;
    }

    setProcessing(true);
    try {
      const endpoint =
        moneyModal.type === "add"
          ? `/admin/client/${moneyModal.client._id}/add`
          : `/admin/client/${moneyModal.client._id}/withdraw`;
      await API.post(endpoint, { amount, note: moneyModal.note || "" });
      setMoneyModal({ ...moneyModal, open: false, amount: "", note: "" });
      await loadClients();
    } catch (err) {
      setError(err.response?.data?.message || "Transaction failed");
    } finally {
      setProcessing(false);
    }
  };

  // Transactions Modal
  const openTxModal = async (client) => {
    setTxModal({
      open: true,
      client,
      transactions: [],
      loading: true,
      txSearch: "",
      typeFilter: "all",
    });

    try {
      const res = await API.get(`/admin/transactionsById`, {
        params: { userId: client._id },
      });
      setTxModal((prev) => ({
        ...prev,
        transactions: res.data || [],
        loading: false,
      }));
    } catch {
      setError("Failed to load transactions");
      setTxModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const filteredTx = txModal.transactions.filter((tx) => {
    const q = (txModal.txSearch || "").toLowerCase();
    const match =
      (tx.type || "").toLowerCase().includes(q) ||
      (tx.note || "").toLowerCase().includes(q) ||
      (tx.commissionKind || "").toLowerCase().includes(q) ||
      (tx.status || "").toLowerCase().includes(q);
    const typeMatch =
      txModal.typeFilter === "all" || tx.type === txModal.typeFilter;
    return match && typeMatch;
  });

  const formatDate = (date) =>
    new Date(date).toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "short",
    });

  const getStatusDisplay = (tx) => {
    const status = (tx.status || "approved").toLowerCase();

    if (status === "pending") {
      return {
        element: (
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 rounded-full">
            <Clock className="w-4 h-4 text-yellow-700" />
            <span className="font-semibold text-yellow-700 text-sm">PENDING</span>
          </div>
        ),
        showAmount: false,
      };
    }

    if (status === "rejected") {
      return {
        element: (
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 rounded-full">
            <XCircle className="w-4 h-4 text-red-700" />
            <span className="font-semibold text-red-700 text-sm">REJECTED</span>
          </div>
        ),
        showAmount: false,
      };
    }

    return {
      element: null,
      showAmount: true,
    };
  };

  const typeMeta = (tx) => {
    if (tx.type === "deposit")
      return {
        label: "Deposit",
        Icon: ArrowUpRight,
        amountPrefix: "+",
        amountColor: "text-emerald-600",
        bgColor: "bg-emerald-50",
      };
    if (tx.type === "withdraw")
      return {
        label: "Withdraw",
        Icon: ArrowDownRight,
        amountPrefix: "-",
        amountColor: "text-red-600",
        bgColor: "bg-red-50",
      };
    return {
      label:
        tx.commissionKind === "signup"
          ? "Signup Commission"
          : "Transaction Commission",
      Icon: Gift,
      amountPrefix: "+",
      amountColor: "text-indigo-600",
      bgColor: "bg-indigo-50",
    };
  };

  const resetPassword = async (id, newPassword) => {
    try {
      await API.put(`https://api.kestren.com/api/auth/client/${id}/reset-password`, { newPassword });
      alert("Password Updated Successfully");
      await loadClients();
    } catch (err) {
      console.error("Failed to reset password:", err);
    }
  };


  const [editModal, setEditModal] = useState({
    open: false,
    client: null,
    form: {},
    qrPreview: null,
  });

  const openEditModal = (client) => {
    setEditModal({
      open: true,
      client,
      form: {
        name: client.name || "",
        email: client.email || "",
        phone: client.phone || "",
        gender: client.gender || "",
        profession: client.profession || "",
        walletAddress: client.walletAddress || "",
        status: client.status,
      },
      qrPreview: `https://api.kestren.com${client.qrImage}`,
    });
  };

  const handleEditInput = (key, value) => {
    setEditModal((prev) => ({
      ...prev,
      form: { ...prev.form, [key]: value },
    }));
  };

  const handleQRUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setEditModal((prev) => ({
      ...prev,
      qrPreview: URL.createObjectURL(file),
      qrFile: file,
    }));
  };

  const saveClientChanges = async () => {
    const formData = new FormData();
    const fields = editModal.form;

    Object.keys(fields).forEach((key) => {
      if (fields[key] !== "" && fields[key] !== null && fields[key] !== undefined) {
        formData.append(key, fields[key]);
      }
    });

    if (editModal.qrFile) {
      formData.append("qrImage", editModal.qrFile);
    }

    try {
      await API.put(`/admin/client/${editModal.client._id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Client updated successfully");
      setEditModal({ open: false, client: null, form: {} });
      await loadClients();
    } catch (err) {
      console.error(err);
      alert("Failed to update client");
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Dark Header with Logo */}
      <div className="w-full bg-gray-50 ">
        <div className="max-w-8xl mx-auto px-6 py-8 flex flex-col sm:flex-row justify-between items-center gap-6">

          {/* Left Side */}
          <div>
            <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
              Client Management
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Manage all users, wallets & referrals
            </p>
          </div>

          {/* Gray Button */}
          <button
            onClick={loadClients}
            className="flex items-center gap-2 px-6 py-3 
                 bg-gray-200 text-gray-800 
                 rounded-xl hover:bg-gray-300 
                 transition-all shadow-md"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            Refresh List
          </button>

        </div>
      </div>


      {/* Main Content */}
      <div className="flex-1 p-6 md:p-8">
        <div className="max-w-8xl mx-auto lg:pl-8 lg:pr-8">

          {/* Search */}
          <div className="mb-8">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or referral code..."
                className="w-full pl-12 pr-5 py-4 bg-white border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gray-600 transition-all text-gray-800 font-medium shadow-sm"
              />
            </div>
          </div>

          {/* Error */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl flex items-center gap-3 mb-6 shadow-sm">
              <AlertCircle className="w-6 h-6 flex-shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="bg-white rounded-3xl shadow-xl p-16 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-gray-700 mx-auto" />
              <p className="text-gray-600 mt-4 text-lg">Loading clients...</p>
            </div>
          )}

          {/* Desktop Table */}
          {!loading && (
            <>
              <div className="hidden lg:block overflow-x-auto bg-white rounded-3xl shadow-xl border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr className="text-left text-gray-700">
                      <th className="py-5 px-6 font-semibold">Client</th>
                      <th className="py-5 px-6 font-semibold">Email</th>
                      <th className="py-5 px-6 font-semibold">Balance</th>
                      <th className="py-5 px-6 font-semibold">Referrals</th>
                      <th className="py-5 px-6 font-semibold">Status</th>
                      <th className="py-5 px-6 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-16 text-gray-500 text-lg">
                          No clients found
                        </td>
                      </tr>
                    ) : (
                      filteredClients.map((c) => (
                        <tr
                          key={c._id}
                          className="border-t border-gray-100 hover:bg-gray-50 transition-all"
                        >
                          <td className="py-5 px-6 font-semibold text-gray-800">{c.name}</td>
                          <td className="py-5 px-6 text-gray-600">{c.email}</td>
                          <td className="py-5 px-6">
                            <div className="flex items-center font-bold text-emerald-600 gap-1">
                              <DollarSign className="w-5 h-5" />
                              {Number(c.balance || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="py-5 px-6 text-gray-700">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{c?.referral?.referredCount ?? 0}</span>
                              <span className="text-gray-500">users •</span>
                              <DollarSign className="w-4 h-4" />
                              <span className="font-semibold">{Number(c?.referral?.earnings || 0).toLocaleString()}</span>
                            </div>
                          </td>
                          <td className="py-5 px-6">
                            <button
                              onClick={() => toggleStatus(c._id, c.status)}
                              disabled={statusUpdatingId === c._id}
                              className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${c.status
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-red-100 text-red-700"
                                }`}
                            >
                              {statusUpdatingId === c._id ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Updating...
                                </>
                              ) : (
                                c.status ? "Active" : "Inactive"
                              )}
                            </button>

                          </td>
                          <td className="py-5 px-6">
                            <div className="flex flex-wrap gap-3">
                              <button
                                onClick={() => setViewModal({ open: true, client: c })}
                                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium flex items-center gap-2"
                              >
                                <UserCheck className="w-4 h-4" /> View
                              </button>
                              <button
                                onClick={() => openMoneyModal("add", c)}
                                className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 text-sm font-medium flex items-center gap-2"
                              >
                                <Plus className="w-4 h-4" /> Add
                              </button>
                              <button
                                onClick={() => openMoneyModal("withdraw", c)}
                                className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 text-sm font-medium flex items-center gap-2"
                              >
                                <Minus className="w-4 h-4" /> Withdraw
                              </button>
                              <button
                                onClick={() => openTxModal(c)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium flex items-center gap-2"
                              >
                                <Wallet className="w-4 h-4" /> History
                              </button>

                              {/* Edit */}
                              <button
                                onClick={() => openEditModal(c)}

                                className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm font-medium flex items-center gap-2"
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => handleDeleteClient(c._id)}
                                className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-5">
                {filteredClients.length === 0 ? (
                  <div className="bg-white rounded-3xl shadow-xl p-10 text-center text-gray-500 text-lg">
                    No clients found
                  </div>
                ) : (
                  filteredClients.map((c) => (
                    <div
                      key={c._id}
                      className="bg-white rounded-3xl shadow-xl p-6 border border-gray-200"
                    >
                      <div className="flex justify-between items-start mb-5">
                        <div>
                          <h3 className="font-bold text-xl text-gray-800">{c.name}</h3>
                          <p className="text-gray-600 text-sm mt-1">{c.email}</p>
                        </div>
                        <button
                          onClick={() => toggleStatus(c._id, c.status)}
                          className={`px-4 py-2 rounded-full text-sm font-medium ${c.status
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                            }`}
                        >
                          {c.status ? "Active" : "Inactive"}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                          <p className="text-xs text-gray-600 mb-1">Balance</p>
                          <p className="font-bold text-emerald-600 text-lg flex items-center justify-center gap-1">
                            <DollarSign className="w-5 h-5" />
                            {Number(c.balance || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-4 text-center">
                          <p className="text-xs text-gray-600 mb-1">Referrals</p>
                          <p className="font-bold text-gray-800 text-lg">
                            {c?.referral?.referredCount ?? 0}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-4">

                        {/* View Details */}
                        <button
                          onClick={() => setViewModal({ open: true, client: c })}
                          className="w-full py-3 bg-blue-100 text-blue-700 rounded-xl 
               font-medium flex items-center justify-center gap-2"
                        >
                          <UserCheck className="w-5 h-5" /> View
                        </button>

                        {/* Transaction History */}
                        <button
                          onClick={() => openTxModal(c)}
                          className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl 
               font-medium flex items-center justify-center gap-2"
                        >
                          <Wallet className="w-5 h-5" /> History
                        </button>

                        {/* Add Money */}
                        <button
                          onClick={() => openMoneyModal("add", c)}
                          className="w-full py-3 bg-emerald-100 text-emerald-700 rounded-xl 
               font-medium flex items-center justify-center gap-2"
                        >
                          <Plus className="w-5 h-5" /> Add
                        </button>

                        {/* Withdraw */}
                        <button
                          onClick={() => openMoneyModal("withdraw", c)}
                          className="w-full py-3 bg-amber-100 text-amber-700 rounded-xl 
               font-medium flex items-center justify-center gap-2"
                        >
                          <Minus className="w-5 h-5" /> Withdraw
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() => openEditModal(c)}
                          className="w-full py-3 bg-purple-100 text-purple-700 rounded-xl 
               font-medium flex items-center justify-center gap-2"
                        >
                          <Edit className="w-4 h-4" /> Edit
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDeleteClient(c._id)}
                          className="w-full py-3 bg-red-100 text-red-700 rounded-xl 
               font-medium flex items-center justify-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>

                      </div>

                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Money Modal */}
      {moneyModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
            <button
              onClick={() => setMoneyModal({ ...moneyModal, open: false })}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-bold text-center mb-6">
              {moneyModal.type === "add" ? "Add Money" : "Withdraw Money"}
              <p className="text-sm text-gray-600 mt-2">
                Client: <strong>{moneyModal.client?.name}</strong>
              </p>
            </h2>
            <input
              type="number"
              placeholder="Amount"
              value={moneyModal.amount}
              onChange={(e) => setMoneyModal({ ...moneyModal, amount: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4"
            />
            <input
              type="text"
              placeholder="Note (optional)"
              value={moneyModal.note}
              onChange={(e) => setMoneyModal({ ...moneyModal, note: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4"
            />
            <button
              onClick={handleMoney}
              disabled={processing}
              className={`w-full py-4 rounded-xl text-white font-bold ${processing ? "bg-gray-400" : moneyModal.type === "add" ? "bg-emerald-600" : "bg-red-600"}`}
            >
              {processing ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : `${moneyModal.type === "add" ? "Add" : "Withdraw"} $${moneyModal.amount || 0}`}
            </button>
          </div>
        </div>
      )}

      {/* Transactions Modal */}
      {txModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b bg-gray-50 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Transaction History</h2>
                <p className="text-sm text-gray-600">
                  {txModal.client?.name} • Balance: ${Number(txModal.client?.balance || 0).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => setTxModal({ ...txModal, open: false })}
                className="p-2 hover:bg-gray-200 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 border-b bg-gray-50 flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={txModal.txSearch}
                  onChange={(e) => setTxModal({ ...txModal, txSearch: e.target.value })}
                  placeholder="Search transactions..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl"
                />
              </div>
              <select
                value={txModal.typeFilter}
                onChange={(e) => setTxModal({ ...txModal, typeFilter: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-xl"
              >
                <option value="all">All Types</option>
                <option value="deposit">Deposit</option>
                <option value="withdraw">Withdrawal</option>
                <option value="commission">Commission</option>
              </select>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {txModal.loading ? (
                <div className="text-center py-20">
                  <Loader2 className="w-12 h-12 animate-spin text-gray-700 mx-auto" />
                </div>
              ) : filteredTx.length === 0 ? (
                <p className="text-center text-gray-500 py-20 text-lg">No transactions found</p>
              ) : (
                <div className="space-y-4">
                  {filteredTx.map((tx) => {
                    const meta = typeMeta(tx);
                    const statusInfo = getStatusDisplay(tx);

                    return (
                      <div
                        key={tx._id}
                        className={`p-6 rounded-2xl border ${tx.status === "pending"
                          ? "bg-yellow-50 border-yellow-300"
                          : tx.status === "rejected"
                            ? "bg-red-50 border-red-300"
                            : "bg-gray-50 border-gray-200"
                          }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex items-start gap-4">
                            <div className="p-3 bg-white rounded-full shadow">
                              <meta.Icon className="w-6 h-6 text-gray-700" />
                            </div>
                            <div>
                              <p className="font-bold text-lg">{meta.label}</p>
                              <p className="text-sm text-gray-600 mt-1">
                                {formatDate(tx.createdAt || tx.date)}
                              </p>
                              {tx.note && (
                                <p className="text-sm text-gray-600 mt-2">
                                  Note: {tx.note}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="text-right">
                            {tx.status === "pending" ? (
                              <div>
                                <p className="font-bold text-2xl text-yellow-600">
                                  ${Number(tx.amount || 0).toLocaleString()}
                                </p>
                                <div className="mt-2 px-4 py-1 bg-yellow-200 text-yellow-800 rounded-full text-sm font-bold inline-flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  PENDING
                                </div>
                              </div>
                            ) : tx.status === "rejected" ? (
                              <div>
                                <p className="font-bold text-2xl text-red-600">
                                  ${Number(tx.amount || 0).toLocaleString()}
                                </p>
                                <div className="mt-2 px-4 py-1 bg-red-200 text-red-800 rounded-full text-sm font-bold inline-flex items-center gap-2">
                                  <XCircle className="w-4 h-4" />
                                  REJECTED
                                </div>
                              </div>
                            ) : (
                              <p className={`font-bold text-2xl ${meta.amountColor}`}>
                                {meta.amountPrefix}${Number(tx.originalAmount || tx.amount || 0).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Client Modal */}
      <ClientDetailsModal
        viewModal={viewModal}
        setViewModal={setViewModal}
        resetPassword={resetPassword}
      />


      {editModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">

          {/* Scrollable wrapper */}
          <div className="w-full max-h-[90vh] overflow-y-auto">

            <div className="bg-white max-w-3xl w-full rounded-3xl shadow-2xl p-6 md:p-8 mx-auto relative">

              {/* Close Button */}
              <button
                onClick={() => setEditModal({ open: false, client: null })}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Title */}
              <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center">
                Edit Client
              </h2>

              {/* FORM GRID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">

                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold mb-1">Full Name</label>
                  <input
                    type="text"
                    value={editModal.form.name}
                    onChange={(e) => handleEditInput("name", e.target.value)}
                    placeholder="Enter full name"
                    className="w-full border rounded-xl px-4 py-3 text-sm"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold mb-1">Email</label>
                  <input
                    type="email"
                    value={editModal.form.email}
                    onChange={(e) => handleEditInput("email", e.target.value)}
                    placeholder="Enter email"
                    className="w-full border rounded-xl px-4 py-3 text-sm"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold mb-1">Phone</label>
                  <input
                    type="text"
                    value={editModal.form.phone}
                    onChange={(e) => handleEditInput("phone", e.target.value)}
                    placeholder="Enter phone number"
                    className="w-full border rounded-xl px-4 py-3 text-sm"
                  />
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-sm font-semibold mb-1">Gender</label>
                  <input
                    type="text"
                    value={editModal.form.gender}
                    onChange={(e) => handleEditInput("gender", e.target.value)}
                    placeholder="Gender"
                    className="w-full border rounded-xl px-4 py-3 text-sm"
                  />
                </div>

                {/* Profession */}
                <div>
                  <label className="block text-sm font-semibold mb-1">Profession</label>
                  <input
                    type="text"
                    value={editModal.form.profession}
                    onChange={(e) => handleEditInput("profession", e.target.value)}
                    placeholder="Profession"
                    className="w-full border rounded-xl px-4 py-3 text-sm"
                  />
                </div>

                {/* Wallet */}
                <div>
                  <label className="block text-sm font-semibold mb-1">Wallet Address</label>
                  <input
                    type="text"
                    value={editModal.form.walletAddress}
                    onChange={(e) => handleEditInput("walletAddress", e.target.value)}
                    placeholder="Wallet Address"
                    className="w-full border rounded-xl px-4 py-3 text-sm"
                  />
                </div>

                {/* QR Upload */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-2">QR Image</label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleQRUpload}
                    className="block w-full text-sm"
                  />

                  {editModal.qrPreview && (
                    <div className="mt-3 flex justify-center md:justify-start">
                      <img
                        src={editModal.qrPreview}
                        alt="QR Preview"
                        className="w-32 h-32 object-cover rounded-xl border"
                      />
                    </div>
                  )}
                </div>

              </div>

              {/* Save Button */}
              <button
                onClick={saveClientChanges}
                className="w-full mt-8 py-4 bg-blue-600 text-white rounded-xl 
                     font-bold hover:bg-blue-700 text-lg"
              >
                Save Changes
              </button>

            </div>
          </div>
        </div>
      )}





    </div>
  );
}